import type { AxiosInstance } from 'axios';
import type { Question, QuestionChoice } from '@/models/question';
import type { QuestionTypeMapping } from '@/models/questionType';
import type { SurveyPage } from '@/models/surveyPage';
import { buildQuestionChoicesRelation } from '@/utils/questions';

export type UpdatePage = {
   pageId: string;
   data: Partial<SurveyPage>;
};

export type CreateQuestion = {
   pageId: string;
   question: Partial<Question>;
   questionTypesMapping: QuestionTypeMapping;
};

export type CreateQuestionChoice = {
   pageId: string;
   questionId: string;
   questionChoice: Partial<QuestionChoice>;
};

export type UpdateQuestion = {
   pageId: string;
   questionId: string;
   question: Partial<Question>;
   questionTypesMapping: QuestionTypeMapping;
};

export type UpdateAllQuestions = {
   pageId: string;
   questions: Partial<Question>[];
};

export type UpdateQuestionChoice = {
   pageId: string;
   questionId: string;
   questionChoiceId: string;
   questionChoice: Partial<Omit<QuestionChoice, 'answers_limit'>> & {
      answers_limit: number | null;
   };
};

class PageService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async updatePage({ pageId, data }: UpdatePage): Promise<void> {
      await this.provider.post('/rest/pages/mutate', {
         mutate: [
            {
               operation: 'update',
               key: pageId,
               attributes: data,
            },
         ],
      });
   }

   async duplicatePage(pageId: string): Promise<void> {
      await this.provider.post('rest/pages/actions/duplicate', {
         fields: [
            {
               name: 'page_id',
               value: pageId,
            },
         ],
      });
   }

   async deletePage(id: string): Promise<void> {
      await this.provider.delete(`/pages/${id}`);
   }

   async createQuestion({
      pageId,
      question,
      questionTypesMapping,
   }: CreateQuestion): Promise<void> {
      const questionChoicesRelation = buildQuestionChoicesRelation({
         questionTypeId: question.question_type_id,
         questionTypesMapping,
      });

      const personalization = this.buildQuestionPersonalization(
         question,
         questionTypesMapping,
      );

      const enrichedQuestion = personalization
         ? { ...question, personalization }
         : question;

      await this.provider.post('rest/pages/mutate', {
         mutate: [
            {
               operation: 'update',
               key: pageId,
               relations: {
                  questions: [
                     {
                        operation: 'create',
                        attributes: enrichedQuestion,
                        ...(questionChoicesRelation ?? {}),
                     },
                  ],
               },
            },
         ],
      });
   }

   async updateQuestion({
      pageId,
      questionId,
      question,
      questionTypesMapping,
   }: UpdateQuestion): Promise<void> {
      const questionChoicesRelation = buildQuestionChoicesRelation({
         questionTypeId: question.question_type_id,
         questionTypesMapping,
      });

      await this.provider.post('rest/pages/mutate', {
         mutate: [
            {
               operation: 'update',
               key: pageId,
               relations: {
                  questions: [
                     {
                        operation: 'update',
                        key: questionId,
                        attributes: question,
                        ...(questionChoicesRelation ?? {}),
                     },
                  ],
               },
            },
         ],
      });
   }

   async updateAllQuestions({
      pageId,
      questions,
   }: UpdateAllQuestions): Promise<void> {
      await this.provider.post('/rest/pages/mutate', {
         mutate: [
            {
               operation: 'update',
               key: pageId,
               relations: {
                  questions: questions
                     .filter((question) => Boolean(question?.id))
                     .map((question) => {
                        const { id, linked_choices, ...attributes } = question;

                        return {
                           operation: 'update',
                           key: id,
                           attributes,
                           relations: {
                              questionChoices: (linked_choices ?? [])
                                 .filter((questionChoice) =>
                                    Boolean(questionChoice?.id),
                                 )
                                 .map(({ id, label, answers_limit }) => ({
                                    operation: 'update',
                                    key: String(id),
                                    attributes: {
                                       label: label,
                                       answers_limit: answers_limit,
                                    },
                                 })),
                           },
                        };
                     }),
               },
            },
         ],
      });
   }

   private buildQuestionPersonalization(
      question: Partial<Question>,
      questionTypesMapping: Record<string, string>,
   ) {
      const isNumberQuestion =
         question.question_type_id === questionTypesMapping.number;
      const isDatetimeQuestion =
         question.question_type_id === questionTypesMapping.datetime;

      if (isDatetimeQuestion) {
         return { option: 'date' };
      }

      if (isNumberQuestion) {
         return { option: 'number' };
      }

      return undefined;
   }
}

export default PageService;
